import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Alert, AlertEngineInfo, AlertSeverity, FallbackTier } from "@chula/shared";
import { ALERT_SEVERITY_RANK } from "@chula/shared";
import { API_BASE } from "../lib/apiBase";
import { operatorFetch } from "../lib/operatorToken";

export const ALERTS_POLL_MS = 30_000;
const LIST_PATH = "/api/alerts?status=open,acknowledged&limit=200";

export type AlertAction = "ack" | "assign" | "resolve" | "note";

export interface AlertSummary {
  /** Unresolved alerts. */
  total: number;
  bySeverity: Record<AlertSeverity, number>;
  /** Highest unresolved severity, null when all clear. */
  highest: AlertSeverity | null;
}

export interface UseAlertsResult {
  alerts: Alert[];
  engine: AlertEngineInfo | null;
  tier: FallbackTier | "loading";
  error: string | null;
  /** Last failed operator action, human-readable (auth, conflict, network). */
  actionError: string | null;
  summary: AlertSummary;
  refetch: () => Promise<void>;
  acknowledge: (id: string, note?: string) => Promise<boolean>;
  assign: (id: string, assignee: string, note?: string) => Promise<boolean>;
  resolve: (id: string, note?: string) => Promise<boolean>;
  addNote: (id: string, note: string) => Promise<boolean>;
}

interface ListResponse {
  features?: Alert[];
  meta?: { fallbackTier?: FallbackTier };
  engine?: AlertEngineInfo;
}

/** Pure: counts of unresolved alerts for a top-bar badge ("ATTENTION · N" / "ALL CLEAR"). */
export function summariseAlerts(alerts: readonly Alert[]): AlertSummary {
  const bySeverity: Record<AlertSeverity, number> = { critical: 0, warning: 0, watch: 0, info: 0 };
  let highest: AlertSeverity | null = null;
  let total = 0;
  for (const a of alerts) {
    if (a.status === "resolved") continue;
    bySeverity[a.severity]++;
    total++;
    if (highest === null || ALERT_SEVERITY_RANK[a.severity] > ALERT_SEVERITY_RANK[highest]) highest = a.severity;
  }
  return { total, bySeverity, highest };
}

/** The local state an action produces, applied before the server confirms. */
export function optimistic(a: Alert, action: AlertAction, arg?: string): Alert {
  const now = new Date().toISOString();
  if (action === "ack") return { ...a, status: "acknowledged", acknowledgedAt: now };
  if (action === "assign") return { ...a, status: "acknowledged", assignee: arg ?? a.assignee, acknowledgedAt: a.acknowledgedAt ?? now };
  if (action === "resolve") return { ...a, status: "resolved", resolvedAt: now };
  return a;
}

function describeFailure(status: number, body: { error?: string } | null): string {
  if (status === 401) return "Operator sign-in required — use the Operator button in the top bar.";
  if (status === 503) return body?.error ?? "Server not configured for operator actions.";
  if (status === 409) return body?.error ?? "Someone else already changed this alert.";
  if (status === 429) return "Too many actions — wait a minute.";
  return body?.error ?? `Action failed (${status}).`;
}

export function useAlerts(pollMs = ALERTS_POLL_MS): UseAlertsResult {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [engine, setEngine] = useState<AlertEngineInfo | null>(null);
  const [tier, setTier] = useState<FallbackTier | "loading">("loading");
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const alive = useRef(true);
  const alertsRef = useRef<Alert[]>([]);
  alertsRef.current = alerts;

  const refetch = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}${LIST_PATH}`, { cache: "no-store" });
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      const json = (await res.json()) as ListResponse;
      if (!Array.isArray(json.features)) throw new Error("Invalid alerts response");
      if (!alive.current) return;
      setAlerts(json.features);
      setEngine(json.engine ?? null);
      setTier(json.meta?.fallbackTier ?? "database");
      setError(null);
    } catch (err) {
      if (!alive.current) return;
      setError((err as Error).message);
      setTier("unavailable");
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    void refetch();
    const id = setInterval(() => { void refetch(); }, pollMs);
    return () => {
      alive.current = false;
      clearInterval(id);
    };
  }, [refetch, pollMs]);

  const act = useCallback(async (id: string, action: AlertAction, body: Record<string, string>, arg?: string) => {
    const before = alertsRef.current.find((a) => a.id === id);
    if (before && action !== "note") {
      setAlerts((list) => list.map((a) => (a.id === id ? optimistic(a, action, arg) : a)));
    }
    setActionError(null);
    try {
      const res = await operatorFetch(`${API_BASE}/api/alerts/${encodeURIComponent(id)}/${action}`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body),
      });
      const json = (await res.json().catch(() => null)) as { alert?: Alert; error?: string } | null;
      if (!res.ok || !json?.alert) throw Object.assign(new Error(describeFailure(res.status, json)), { handled: true });
      const confirmed = json.alert;
      if (alive.current) setAlerts((list) => list.map((a) => (a.id === id ? confirmed : a)));
      return true;
    } catch (err) {
      if (!alive.current) return false;
      if (before) setAlerts((list) => list.map((a) => (a.id === id ? before : a)));
      const e = err as Error & { handled?: boolean };
      setActionError(e.handled ? e.message : "Network error — action not saved.");
      return false;
    }
  }, []);

  const withNote = (note?: string): Record<string, string> => (note && note.trim() ? { note: note.trim() } : {});

  const acknowledge = useCallback((id: string, note?: string) => act(id, "ack", withNote(note)), [act]);
  const assign = useCallback(
    (id: string, assignee: string, note?: string) => act(id, "assign", { assignee: assignee.trim(), ...withNote(note) }, assignee.trim()),
    [act],
  );
  const resolve = useCallback((id: string, note?: string) => act(id, "resolve", withNote(note)), [act]);
  const addNote = useCallback((id: string, note: string) => act(id, "note", { note: note.trim() }), [act]);

  const summary = useMemo(() => summariseAlerts(alerts), [alerts]);

  return { alerts, engine, tier, error, actionError, summary, refetch, acknowledge, assign, resolve, addNote };
}
